// What the payer was doing before the bank took the page away.
//
// A gateway payment is a full navigation off our origin and back onto a bare
// `/gateway-callback/`: React state, the router's `location.state` and every
// hook that knew WHAT was being paid for are gone by the time the user
// returns. The backend's callback carries the outcome (`result`, `code`,
// `amount`, `gateway`) but it cannot tell us that this was an اشتراک rather
// than a قسط — so `payment_succeeded` had nothing to attribute itself to and
// the funnel ended in one undifferentiated blob.
//
// So every site that hands the user to a gateway leaves a note here first, and
// the callback page reads it, attaches it to the terminal event, and clears
// it.

const KEY = 'paymentFlowContext'

/**
 * Older than this and the note belongs to an attempt that was abandoned at the
 * bank. Labelling the next payment with it would be worse than admitting we do
 * not know which flow it came from.
 */
const MAX_AGE_MS = 2 * 60 * 60 * 1000

export interface PaymentFlow {
    /** the same value the site puts on its own `payment_gateway_redirect` */
    flow_type: string
    amount?: number | null
    gateway?: string | null
    box_id?: number | string | null
    /** 'mnp' | 'csb', when the payment is about exactly one box */
    box_type?: string | null
    /** subscription flows only, so the purchase event can name the period */
    month_count?: number | null
    ts: number
}

/** The flows whose success is a subscription purchase. */
export const isSubscriptionFlow = (flowType?: string | null): boolean =>
    flowType === 'subscription' || flowType === 'cashbox_subscription'

/** Called immediately before `window.location.replace(payment_link)`. */
export const rememberPaymentFlow = (flow: Omit<PaymentFlow, 'ts'>) => {
    try {
        localStorage.setItem(KEY, JSON.stringify({ ...flow, ts: Date.now() }))
    } catch {
        // Storage can be full or blocked (private windows, a few in-app
        // webviews). This runs one line before the redirect to the bank, so a
        // throw here would cost the user their payment to save a property on
        // an event.
    }
}

export const readPaymentFlow = (): PaymentFlow | null => {
    try {
        const raw = localStorage.getItem(KEY)
        if (!raw) return null
        const flow: PaymentFlow = JSON.parse(raw)
        if (!flow?.flow_type || !flow.ts) return null
        return Date.now() - flow.ts > MAX_AGE_MS ? null : flow
    } catch {
        return null
    }
}

export const clearPaymentFlow = () => {
    try {
        localStorage.removeItem(KEY)
    } catch {
        // Same reason as above: never let bookkeeping break the screen.
    }
}
